const round = (value) => Number(Number(value || 0).toFixed(4));
const text = (value) => String(value ?? '').trim();
const meterUnit = (unit) => /^(m|mt|mts|mtr|mtrs|metro|metros)\.?$/i.test(text(unit)) ? 'mtrs' : text(unit);

// Purchase needs include every article's bill of materials once. Cutting's
// additional display materials are deliberately not another purchase source.
export function buildNeedsByProcess(model = {}) {
  const groups = new Map();
  for (const [articleIndex, item] of (model.items || []).entries()) {
    for (const [lineIndex, line] of (item.materials || []).entries()) {
      const process = text(line.process_name) || 'Sin proceso';
      const processKey = line.process_id || process;
      if (!groups.has(processKey)) groups.set(processKey, { process, process_code: line.process_code || '', materials: new Map() });
      const group = groups.get(processKey);
      const unit = meterUnit(line.unit);
      const key = JSON.stringify([
        line.material_id || line.material_code || line.source_line_id || `${articleIndex}:${lineIndex}`,
        line.supplier_id || line.supplier_code || text(line.supplier_name),
        text(line.supplier_ref), text(line.supplier_description), text(line.material_season), unit,
      ]);
      if (!group.materials.has(key)) group.materials.set(key, {
        name: line.name || 'Material', material_code: line.material_code || '',
        supplier_name: line.supplier_name || '', supplier_code: line.supplier_code || '',
        supplier_ref: line.supplier_ref || '', supplier_description: line.supplier_description || '',
        material_season: line.material_season || '', unit,
        notes: new Set(), prices: new Set(), contributions: [], totalBaseQuantity: 0,
        totalWasteQuantity: 0, totalQuantity: 0, totalCost: 0,
      });
      const material = group.materials.get(key);
      if (text(line.notes)) material.notes.add(text(line.notes));
      material.prices.add(Number(line.unit_cost || 0));
      const garments = Number(item.totalQuantity || 0);
      const consumption = Number(line.quantity_per_unit || 0);
      const wastePct = Number(line.waste_pct || 0);
      const base = consumption * garments;
      const total = Number(line.total_quantity ?? base * (1 + wastePct / 100));
      const cost = Number(line.total_cost || 0);
      material.totalBaseQuantity += base;
      material.totalWasteQuantity += total - base;
      material.totalQuantity += total;
      material.totalCost += cost;
      // One contribution per article/material line: differing consumptions,
      // prices and waste percentages remain visible even for the same material.
      const colors = (item.colorLines || []).filter((color) => Number(color.total || 0) > 0);
      material.contributions.push({
        articleKey: item.product_id || `${item.code || ''}:${articleIndex}`,
        code: item.code || '', description: item.description || '', season: item.season || '',
        garments, consumption, wastePct, unitPrice: Number(line.unit_cost || 0),
        baseQuantity: round(base), wasteQuantity: round(total - base), totalQuantity: round(total), totalCost: round(cost),
        notes: line.notes || '',
        colors: colors.map((color) => ({
          color: color.color || '', description: color.description || color.color || '',
          contrast_1: color.contrast_1 || '', contrast_2: color.contrast_2 || '', contrast_3: color.contrast_3 || '',
          garments: Number(color.total),
          totalQuantity: round(garments ? total * Number(color.total) / garments : 0),
        })),
      });
    }
  }
  const result = [...groups.values()].map((group) => {
    const materials = [...group.materials.values()].map((material) => {
      const colors = new Map();
      for (const contribution of material.contributions) {
        for (const color of contribution.colors) {
          const key = JSON.stringify([color.color || color.description, color.contrast_1, color.contrast_2, color.contrast_3]);
          if (!colors.has(key)) colors.set(key, { ...color, totalQuantity: 0 });
          colors.get(key).totalQuantity += color.totalQuantity;
        }
      }
      return {
        ...material, notes: [...material.notes], prices: [...material.prices].sort((a, b) => a - b),
        colorNeeds: [...colors.values()].map((color) => ({ ...color, totalQuantity: round(color.totalQuantity) })),
        articleCount: new Set(material.contributions.map((row) => row.articleKey)).size,
        totalBaseQuantity: round(material.totalBaseQuantity), totalWasteQuantity: round(material.totalWasteQuantity),
        totalQuantity: round(material.totalQuantity), totalCost: round(material.totalCost),
      };
    }).sort((a, b) => `${a.material_code} ${a.name} ${a.supplier_name} ${a.supplier_ref}`.localeCompare(`${b.material_code} ${b.name} ${b.supplier_name} ${b.supplier_ref}`, 'es', { numeric: true }));
    return { process: group.process, process_code: group.process_code, materials, processTotalCost: round(materials.reduce((sum, line) => sum + line.totalCost, 0)) };
  }).sort((a, b) => a.process === 'Sin proceso' ? 1 : b.process === 'Sin proceso' ? -1 : a.process.localeCompare(b.process, 'es'));
  return { groups: result, grandTotalCost: round(result.reduce((sum, group) => sum + group.processTotalCost, 0)) };
}
